// Administration : comptes, rôles, accès par site, journal d'audit, données de démonstration.
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, FlaskConical, KeyRound, Plus, ShieldCheck, UserX, UserCheck } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { dateHeure, depuis } from '../../lib/format.ts';
import {
  Alerte, Badge, Bouton, Champ, Chargement, Dialogue, EntetePage, EtatErreur, EtatVide, Onglets, Panneau, useConfirmation, useNotifier,
} from '../../components/ui.tsx';
import { OngletIa } from './OngletIa.tsx';
import { OngletEntreprise } from './OngletEntreprise.tsx';
import { OngletChantiers } from './OngletChantiers.tsx';
import { OngletSalaries } from './OngletSalaries.tsx';
import { OngletConservation } from './OngletConservation.tsx';
import { OngletExport } from './OngletExport.tsx';
import { useSearchParams } from 'react-router';
import '../../styles/memoire.css';

interface Compte { id: string; email: string; display_name: string; work_phone: string | null; active: boolean; mfa_enabled: boolean; must_change_password: boolean;
  created_at: string; last_login_at: string | null; roles: string[]; nb_sites: number }
interface SiteAcces { id: string; name: string; city: string | null; demo: boolean; autorise: boolean }

const ROLES: { code: string; libelle: string; aide: string }[] = [
  { code: 'technicien', libelle: 'Technicien', aide: 'Diagnostics et interventions sur les sites attribués.' },
  { code: 'referent', libelle: 'Référent technique', aide: 'Tous les sites ; valide la documentation, les exigences et les échelles de plan.' },
  { code: 'charge_affaires', libelle: 'Chargé d’affaires', aide: 'Dossiers, prix et devis sur les sites attribués ; émission des devis.' },
  { code: 'admin', libelle: 'Administrateur', aide: 'Comptes, accès, configuration et journal d’audit.' },
];
const libelleRole = (c: string) => ROLES.find((r) => r.code === c)?.libelle ?? c;

function MotDePasseUneFois({ mdp, email, fermer }: { mdp: string; email: string; fermer: () => void }) {
  const notifier = useNotifier();
  return (
    <Dialogue titre="Mot de passe provisoire" ouvert fermer={fermer} pied={<Bouton variante="principal" onClick={fermer}>J’ai transmis le mot de passe</Bouton>}>
      <div className="pile">
        <Alerte ton="ambre" titre="Affiché une seule fois">Transmettez-le à {email} par un canal sûr (de vive voix, SMS). Il devra le remplacer à sa première connexion.</Alerte>
        <div className="rangee">
          <code className="mono" style={{ fontSize: 'var(--t-l)', padding: '8px 12px', background: 'var(--surface-2)', borderRadius: 6, userSelect: 'all' }}>{mdp}</code>
          <Bouton compact icone={<Copy aria-hidden />} onClick={() => navigator.clipboard?.writeText(mdp).then(() => notifier('Copié.'), () => notifier('Copie impossible : sélectionnez le texte.', true))}>Copier</Bouton>
        </div>
      </div>
    </Dialogue>
  );
}

function FormulaireCompte({ compte, fermer, enregistre }: { compte?: Compte; fermer: () => void; enregistre: (mdp?: { mdp: string; email: string }) => void }) {
  const [email, setEmail] = useState(compte?.email ?? '');
  const [nom, setNom] = useState(compte?.display_name ?? '');
  const [telephone, setTelephone] = useState(compte?.work_phone ?? '');
  const [roles, setRoles] = useState<string[]>(compte?.roles ?? ['technicien']);
  const sites = useQuery({
    queryKey: ['admin-sites', compte?.id ?? 'nouveau'],
    queryFn: () => compte ? api.get<SiteAcces[]>(`/api/admin/utilisateurs/${compte.id}/sites`)
      : api.get<any[]>('/api/sites').then((l) => l.map((s) => ({ id: s.id, name: s.name, city: s.city, demo: s.demo, autorise: false }))),
  });
  const [choisis, setChoisis] = useState<Set<string> | null>(null);
  const selection = choisis ?? new Set((sites.data ?? []).filter((s) => s.autorise).map((s) => s.id));
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const voitTout = roles.includes('admin') || roles.includes('referent');

  const valider = async () => {
    setErreur(null); setEnvoi(true);
    try {
      if (compte) {
        await api.patch(`/api/admin/utilisateurs/${compte.id}`, { displayName: nom, roles, siteIds: [...selection], workPhone: telephone.trim() || null });
        enregistre();
      } else {
        const r = await api.post<{ motDePasseProvisoire: string }>('/api/admin/utilisateurs', { email, displayName: nom, roles, siteIds: [...selection], workPhone: telephone.trim() || null });
        enregistre({ mdp: r.motDePasseProvisoire, email });
      }
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };

  return (
    <Dialogue titre={compte ? `Modifier ${compte.display_name}` : 'Créer un compte'} ouvert fermer={fermer} large
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" chargement={envoi} onClick={valider}>{compte ? 'Enregistrer' : 'Créer le compte'}</Bouton></>}>
      <div className="pile">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <div className="grille-form">
          <Champ libelle="Prénom et nom" value={nom} onChange={(e) => setNom(e.target.value)}
            aide="Exemple : Paul Martin (ou MARTIN Paul : le nom écrit en capitales est reconnu). Montré aux collègues selon l’onglet « Données des salariés »." />
          <Champ libelle="Adresse e-mail professionnelle" type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={!!compte} />
          <Champ libelle="Téléphone professionnel (facultatif)" type="tel" inputMode="tel" maxLength={40} value={telephone} onChange={(e) => setTelephone(e.target.value)}
            aide="Montré aux collègues ayant accès au site, dans le carnet ; masqué si le compte est désactivé. Jamais un numéro personnel." />
        </div>
        <fieldset className="pile" style={{ border: 0, padding: 0, margin: 0, gap: 4 }}>
          <legend className="champ-libelle" style={{ marginBottom: 6 }}>Rôles (cumulables)</legend>
          {ROLES.map((r) => (
            <label key={r.code} className="case">
              <input type="checkbox" checked={roles.includes(r.code)} onChange={(e) => setRoles(e.target.checked ? [...roles, r.code] : roles.filter((x) => x !== r.code))} />
              <span><strong>{r.libelle}</strong> <span className="petit discret">— {r.aide}</span></span>
            </label>
          ))}
        </fieldset>
        <fieldset className="pile" style={{ border: 0, padding: 0, margin: 0, gap: 4 }}>
          <legend className="champ-libelle" style={{ marginBottom: 6 }}>Sites autorisés</legend>
          {voitTout && <Alerte ton="info">Avec le rôle administrateur ou référent technique, ce compte voit tous les sites.</Alerte>}
          {sites.isLoading && <Chargement lignes={2} />}
          {sites.data?.length === 0 && <p className="petit discret">Aucun site pour le moment.</p>}
          <div className="grille-acces">
            {sites.data?.map((s) => (
              <label key={s.id} className="case">
                <input type="checkbox" checked={selection.has(s.id)} disabled={voitTout}
                  onChange={(e) => { const n = new Set(selection); if (e.target.checked) n.add(s.id); else n.delete(s.id); setChoisis(n); }} />
                <span>{s.name}{s.city ? <span className="petit discret"> — {s.city}</span> : null}{s.demo ? <span className="petit discret"> (démo)</span> : null}</span>
              </label>
            ))}
          </div>
        </fieldset>
      </div>
    </Dialogue>
  );
}

function OngletComptes() {
  const { moi } = useSession();
  const qc = useQueryClient();
  const notifier = useNotifier();
  const { confirmer, element } = useConfirmation();
  const q = useQuery({ queryKey: ['admin-comptes'], queryFn: () => api.get<Compte[]>('/api/admin/utilisateurs') });
  const [edition, setEdition] = useState<Compte | 'nouveau' | null>(null);
  const [mdp, setMdp] = useState<{ mdp: string; email: string } | null>(null);
  const recharger = () => qc.invalidateQueries({ queryKey: ['admin-comptes'] });

  const basculer = async (c: Compte) => {
    if (c.active && !(await confirmer(`Désactiver ${c.display_name} ?`, 'Ses sessions sont fermées immédiatement. Son historique reste consultable.', 'Désactiver le compte'))) return;
    try { await api.patch(`/api/admin/utilisateurs/${c.id}`, { active: !c.active }); notifier(c.active ? 'Compte désactivé.' : 'Compte réactivé.'); recharger(); }
    catch (e) { notifier(messageErreur(e), true); }
  };
  const reinitialiserMfa = async (c: Compte) => {
    if (!(await confirmer(`Réinitialiser la double authentification de ${c.display_name} ?`, 'À utiliser si son téléphone est perdu ou remplacé. Ses sessions sont fermées ; il la réactivera à sa prochaine connexion.', 'Réinitialiser'))) return;
    try { await api.post(`/api/admin/utilisateurs/${c.id}/reinitialiser-mfa`); notifier('Double authentification réinitialisée.'); recharger(); }
    catch (e) { notifier(messageErreur(e), true); }
  };
  const reinitialiser = async (c: Compte) => {
    if (!(await confirmer(`Réinitialiser le mot de passe de ${c.display_name} ?`, 'Un mot de passe provisoire sera affiché une fois ; ses sessions en cours seront fermées.', 'Réinitialiser'))) return;
    try { const r = await api.post<{ motDePasseProvisoire: string }>(`/api/admin/utilisateurs/${c.id}/reinitialiser`); setMdp({ mdp: r.motDePasseProvisoire, email: c.email }); recharger(); }
    catch (e) { notifier(messageErreur(e), true); }
  };

  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const actifs = q.data!.filter((c) => c.active).length;
  return (
    <Panneau serre titre={`${actifs} compte${actifs > 1 ? 's' : ''} actif${actifs > 1 ? 's' : ''}`}
      actions={<Bouton variante="principal" compact icone={<Plus aria-hidden />} onClick={() => setEdition('nouveau')}>Créer un compte</Bouton>}>
      <div className="liste">
        {q.data!.map((c) => (
          <div key={c.id} className="liste-element" style={{ flexWrap: 'wrap' }}>
            <span className="liste-element-corps" style={{ minWidth: 220 }}>
              <span className="liste-element-titre">{c.display_name}{c.id === moi.utilisateur.id && <span className="petit discret"> (vous)</span>}</span>
              <span className="petit discret">{c.email} — {c.roles.map(libelleRole).join(', ')}
                {!c.roles.includes('admin') && !c.roles.includes('referent') ? ` — ${c.nb_sites} site${c.nb_sites > 1 ? 's' : ''}` : ' — tous les sites'}</span>
              <span className="tres-petit discret">{c.last_login_at ? `Dernière connexion ${depuis(c.last_login_at)}` : 'Jamais connecté'}</span>
            </span>
            {!c.active && <Badge ton="neutre">Désactivé</Badge>}
            {c.must_change_password && c.active && <Badge ton="ambre">Mot de passe provisoire</Badge>}
            {c.mfa_enabled && <Badge ton="succes" icone={<ShieldCheck size={14} aria-hidden />}>Double authentification</Badge>}
            <span className="groupe-btn">
              <Bouton compact onClick={() => setEdition(c)}>Modifier</Bouton>
              <Bouton compact variante="fantome" icone={<KeyRound aria-hidden />} onClick={() => reinitialiser(c)}>Mot de passe</Bouton>
              {c.mfa_enabled && <Bouton compact variante="fantome" icone={<ShieldCheck aria-hidden />} onClick={() => reinitialiserMfa(c)}>Double authentification</Bouton>}
              {c.id !== moi.utilisateur.id && (
                <Bouton compact variante={c.active ? 'danger' : 'secondaire'} icone={c.active ? <UserX aria-hidden /> : <UserCheck aria-hidden />} onClick={() => basculer(c)}>
                  {c.active ? 'Désactiver' : 'Réactiver'}</Bouton>
              )}
            </span>
          </div>
        ))}
      </div>
      {edition && <FormulaireCompte compte={edition === 'nouveau' ? undefined : edition} fermer={() => setEdition(null)}
        enregistre={(m) => { setEdition(null); recharger(); if (m) setMdp(m); else notifier('Compte mis à jour.'); }} />}
      {mdp && <MotDePasseUneFois {...mdp} fermer={() => setMdp(null)} />}
      {element}
    </Panneau>
  );
}

function OngletAudit() {
  const q = useQuery({ queryKey: ['admin-audit'], queryFn: () => api.get<any[]>('/api/admin/audit') });
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  if (!q.data!.length) return <EtatVide titre="Aucun événement" />;
  return (
    <Panneau serre titre="Journal d’audit (100 derniers événements)">
      <div className="tableau-conteneur">
        <table className="tableau">
          <thead><tr><th>Date</th><th>Personne</th><th>Action</th><th className="expert-seulement">Objet</th><th className="masque-etroit">Adresse IP</th></tr></thead>
          <tbody>{q.data!.map((a) => (
            <tr key={a.id}><td className="chiffre">{dateHeure(a.at)}</td><td>{a.display_name ?? <span className="inconnu">Système</span>}</td>
              <td>{a.action.replaceAll('_', ' ')}</td><td className="expert-seulement mono tres-petit">{a.entity}{a.entity_id ? ` ${String(a.entity_id).slice(0, 8)}` : ''}</td>
              <td className="masque-etroit mono tres-petit">{a.ip ?? ''}</td></tr>))}</tbody>
        </table>
      </div>
    </Panneau>
  );
}

function OngletDemonstration() {
  const notifier = useNotifier();
  const qc = useQueryClient();
  const { confirmer, element } = useConfirmation();
  const [envoi, setEnvoi] = useState(false);
  const supprimer = async () => {
    if (!(await confirmer('Supprimer toutes les données de démonstration ?', 'Seront supprimés : tout ce qui est marqué « Démo » (client, site, dossier, pièces, devis, documents) ET tout ce qui a été créé sur le site de démonstration (interventions, dossiers, documents). Les autres sites ne sont pas touchés.', 'Supprimer la démonstration'))) return;
    setEnvoi(true);
    try { await api.del('/api/demo'); notifier('Données de démonstration supprimées.'); await qc.invalidateQueries(); }
    catch (e) { notifier(messageErreur(e), true); } finally { setEnvoi(false); }
  };
  return (
    <Panneau titre="Données de démonstration">
      <div className="pile">
        <p>L’exemple de démonstration (remplacement de tableaux électriques avec exigences complémentaires) est entièrement fictif et signalé par le badge <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge>.</p>
        <div><Bouton variante="danger" chargement={envoi} onClick={supprimer}>Supprimer les données de démonstration</Bouton></div>
      </div>
      {element}
    </Panneau>
  );
}

export function PageAdministration() {
  const { peut } = useSession();
  const [params] = useSearchParams();
  const demande = params.get('onglet');
  const [onglet, setOnglet] = useState<'comptes' | 'entreprise' | 'chantiers' | 'salaries' | 'conservation' | 'export' | 'ia' | 'audit' | 'demo'>(
    demande === 'entreprise' || demande === 'chantiers' || demande === 'salaries' || demande === 'conservation' || demande === 'export' ? demande : 'comptes');
  if (!peut('admin.utilisateurs')) return <EtatErreur erreur={{ code: 'droit_insuffisant', message: 'Cette page est réservée aux administrateurs.' }} />;
  return (
    <div className="pile-l">
      <EntetePage titre="Administration" sousTitre="Comptes nominatifs, rôles, accès par site et traçabilité." />
      <Onglets libelle="Sections d’administration" actif={onglet} changer={setOnglet}
        onglets={[{ id: 'comptes', libelle: 'Comptes' }, { id: 'entreprise', libelle: 'Entreprise' }, { id: 'chantiers', libelle: 'Chantiers' }, { id: 'salaries', libelle: 'Données des salariés (RGPD)' }, { id: 'conservation', libelle: 'Conservation des données' }, { id: 'export', libelle: 'Export complet' }, { id: 'ia', libelle: 'Assistance IA' }, { id: 'audit', libelle: 'Journal d’audit' }, { id: 'demo', libelle: 'Démonstration' }]} />
      {onglet === 'comptes' && <OngletComptes />}
      {onglet === 'entreprise' && <OngletEntreprise />}
      {onglet === 'chantiers' && <OngletChantiers />}
      {onglet === 'salaries' && <OngletSalaries allerConservation={() => setOnglet('conservation')} />}
      {onglet === 'conservation' && <OngletConservation />}
      {onglet === 'export' && <OngletExport />}
      {onglet === 'ia' && <OngletIa />}
      {onglet === 'audit' && <OngletAudit />}
      {onglet === 'demo' && <OngletDemonstration />}
    </div>
  );
}
